// Family Management Types

// Role in family
export type FamilyMemberRole = 'admin' | 'member';

// Status of family member
export type FamilyMemberStatus = 'invited' | 'registered' | 'active' | 'paused' | 'removed';

// Relationship to family admin
export type RelationshipType = 'self' | 'spouse' | 'child' | 'parent' | 'sibling' | 'other';

// Email type for family members
export type EmailType = 'own' | 'derived';

// Role display names
export const FAMILY_ROLE_DISPLAY: Record<FamilyMemberRole, string> = {
  admin: 'Admin',
  member: 'Member',
};

// Status display names
export const FAMILY_STATUS_DISPLAY: Record<FamilyMemberStatus, string> = {
  invited: 'Pending',
  registered: 'Registered',
  active: 'Active',
  paused: 'Paused',
  removed: 'Removed',
};

// Relationship display names (supports additional types from backend)
export const RELATIONSHIP_DISPLAY: Record<string, string> = {
  self: 'Self',
  spouse: 'Spouse',
  child: 'Child',
  parent: 'Parent',
  sibling: 'Sibling',
  grandparent: 'Grandparent',
  grandchild: 'Grandchild',
  in_law: 'In-Law',
  other: 'Other',
};

// Relationship options for forms
export const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'other', label: 'Other' },
] as const;

// Member status configuration with labels and colors
export const MEMBER_STATUS_CONFIG: Record<FamilyMemberStatus, { label: string; color: string }> = {
  invited: { label: 'Invited', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  registered: { label: 'Registered', color: 'bg-teal-100 text-teal-800 border-teal-200' },
  active: { label: 'Active', color: 'bg-green-100 text-green-800 border-green-200' },
  paused: { label: 'Paused', color: 'bg-gray-100 text-gray-800 border-gray-200' },
  removed: { label: 'Removed', color: 'bg-red-100 text-red-800 border-red-200' },
};

// Core Family interface
export interface Family {
  id: string;
  name: string;
  max_members: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  member_count?: number;
  is_active?: boolean;
  // Admin view fields
  admin_name?: string;
  admin_user_id?: string;
  status?: FamilyMemberStatus;
}

// Family Member Profile (nested user info)
export interface FamilyMemberProfile {
  user_profile_id: string;
  client_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  profile_picture?: string | null;
  date_of_birth?: string | null;
  gender?: string;
}

// Family Member interface
export interface FamilyMember {
  id: string;
  family: string; // family id
  family_name?: string;
  user: string | null; // user id (null if derived email not yet registered)
  profile?: FamilyMemberProfile;
  role: FamilyMemberRole;
  status: FamilyMemberStatus;
  relationship: RelationshipType;
  email: string;
  email_type: EmailType;
  display_name?: string | null;
  is_admin: boolean;
  invited_by: string | null;
  invited_at: string | null;
  accepted_at: string | null;
  created_at: string;
  updated_at: string;
}

// Family Dashboard data
export interface FamilyDashboard {
  family: Family;
  members: FamilyMember[];
  pending_invitations: FamilyMember[];
  total_members: number;
  max_members: number;
  can_add_members: boolean;
}

// Individual member health summary
export interface MemberHealthSummary {
  member_id: string;
  member_name: string;
  profile_picture?: string | null;
  recent_biomarkers?: {
    name: string;
    value: string;
    unit: string;
    status: 'optimal' | 'borderline' | 'needs_attention';
    date: string;
  }[];
  upcoming_appointments?: {
    id: string;
    title: string;
    scheduled_at: string;
    provider_name?: string;
  }[];
  active_programs?: {
    id: string;
    name: string;
    progress: number;
  }[];
  last_visit_date?: string | null;
}

// Family health summary (aggregate)
export interface FamilyHealthSummary {
  family_id: string;
  family_name: string;
  members: MemberHealthSummary[];
  total_upcoming_appointments: number;
  total_active_programs: number;
}

// Family Program (available for assignment)
export interface FamilyProgram {
  id: string;
  name: string;
  description?: string;
  duration_days?: number;
  is_active: boolean;
}

// ============================================
// Request Types for API mutations
// ============================================

// Create family request
export interface CreateFamilyRequest {
  name: string;
  max_members?: number;
}

// Add member with own email (sends invitation)
export interface AddMemberOwnEmailRequest {
  family: string;
  email: string;
  first_name: string;
  last_name: string;
  relationship: RelationshipType;
  display_name?: string;
}

// Add member with derived email (sends invitation)
export interface AddMemberDerivedEmailRequest {
  family: string;
  first_name: string;
  last_name: string;
  relationship: RelationshipType;
  display_name: string;
}

// Add existing user to family
export interface AddMemberExistingRequest {
  family: string;
  email: string;
  relationship: RelationshipType;
}

// Upgrade email request
export interface UpgradeEmailRequest {
  new_email: string;
}

// Assign program request
export interface AssignProgramRequest {
  program_id: string;
  start_date?: string;
}

// ============================================
// Response Types
// ============================================

export interface FamilyListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Family[];
}

export interface FamilyMemberListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: FamilyMember[];
}

export interface FamilyProgramListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: FamilyProgram[];
}

// Derived email creation response
export interface DerivedMemberResponse {
  member: FamilyMember;
  generated_email: string;
}

// ============================================
// Helper functions
// ============================================

// Check if user is family admin
export const isFamilyAdmin = (member: FamilyMember): boolean => {
  return member.role === 'admin' || member.is_admin;
};

// Check if member can be removed (not self, not if only admin)
export const canRemoveMember = (
  currentUserMemberId: string,
  targetMember: FamilyMember,
  totalAdmins: number
): boolean => {
  // Can't remove self
  if (currentUserMemberId === targetMember.id) return false;
  // Can't remove if this is the only admin
  if (targetMember.is_admin && totalAdmins <= 1) return false;
  return true;
};

// Check if member has derived email
export const hasDerivedEmail = (member: FamilyMember): boolean => {
  return member.email_type === 'derived';
};

// Get member display name
export const getMemberDisplayName = (member: FamilyMember): string => {
  if (member.display_name) return member.display_name;
  if (member.profile) {
    return `${member.profile.first_name} ${member.profile.last_name}`.trim();
  }
  return member.email;
};

// Get member initials
export const getMemberInitials = (member: FamilyMember): string => {
  if (member.profile) {
    const first = member.profile.first_name?.[0] || '';
    const last = member.profile.last_name?.[0] || '';
    return `${first}${last}`.toUpperCase();
  }
  return member.email[0].toUpperCase();
};

// Get status color class
export const getStatusColorClass = (status: FamilyMemberStatus): string => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'invited':
    case 'registered':
      return 'bg-yellow-100 text-yellow-800';
    case 'paused':
      return 'bg-gray-100 text-gray-800';
    case 'removed':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
